import {
  BoxGeometry,
  CanvasTexture,
  Color,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  SphereGeometry,
  Sprite,
  SpriteMaterial,
  type Material,
  type Scene,
} from 'three';
import { hash2, hash3, hashToUnit } from '../core/math';
import type { LevelBounds, Vector3D } from '../types';
import type { WorldManager } from '../world/WorldManager';
import {
  ENEMY_CATALOG,
  EnemyKind,
  bossKindForStage,
  enemyCountForStage,
  isBossClearTarget,
  isBossKind,
  isBossStage,
  pickEnemyKind,
  stageScale,
  type EnemyDef,
} from './enemyCatalog';

const EPSILON = 1e-6;
/** Approximate player horizontal body radius (matches playerHalfExtents.x ≈ 0.35). */
const PLAYER_BODY_RADIUS = 0.4;
/** Matches DEFAULT_GAME_CONFIG.playerHalfExtents.y for feet-height comparisons. */
const PLAYER_HALF_HEIGHT = 0.9;
/** Extra gap kept between enemy and player surfaces while meleeing. */
const MELEE_GAP = 0.45;
const HIT_FLASH_DURATION = 0.22;
const STUN_DURATION = 0.28;
const KNOCKBACK_SPEED = 11;
const KNOCKBACK_UP = 3.8;
/** Tip-over / shrink / fade before the corpse is removed. */
const DEATH_ANIM_DURATION = 0.58;
const BOSS_DEATH_ANIM_DURATION = 0.9;
const HP_BAR_WIDTH = 64;
const HP_BAR_HEIGHT = 10;

const SENTINEL_ARMOR_MULT = 0.2;
const SENTINEL_SLAM_INTERVAL = 4.0;
const SENTINEL_TELEGRAPH = 0.85;
const SENTINEL_CRACK_DURATION = 2.5;
const SENTINEL_SLAM_RADIUS = 3.2;

const SPIRE_SPORE_INTERVAL = 1.6;
const SPIRE_SPORE_SPEED = 7.5;
const SPIRE_SPORE_DAMAGE_SCALE = 0.55;
const SPIRE_SPORE_LIFE = 3.2;

const HORROR_BURROW_TIME = 1.4;
const HORROR_EMERGE_TELEGRAPH = 0.55;
const HORROR_CHARGE_SPEED = 14;
const HORROR_CHARGE_DAMAGE_SCALE = 1.35;
const HORROR_WALL_STUN = 2.8;
const HORROR_HIT_STUN = 1.1;
const HORROR_CYCLE_GAP = 1.6;

/** Grounded chase jump — clears ~1 block with gravity 28. */
const ENEMY_JUMP_VELOCITY = 8.5;
/** Jump when the chase target is at least this far above the mob center. */
const ENEMY_JUMP_HEIGHT_THRESHOLD = 0.45;
/** Minimum world-unit gap between fauna spawn points. */
const MIN_SPAWN_SEPARATION = 10;
/** How many fauna may actively chase at once (closest win the slots). */
const MAX_ACTIVE_CHASERS = 3;
/** Inside this range an enemy always fights, even if chase slots are full. */
const IMMEDIATE_THREAT_RANGE = 4.25;
/** Soft pack radius while separating so chasers do not form a single blob. */
const PACK_SEPARATION_PADDING = 0.55;

/** Center-to-center distance where chase stops (enemy stands off to attack). */
function stopDistance(enemyRadius: number): number {
  return PLAYER_BODY_RADIUS + enemyRadius + MELEE_GAP;
}

/** Slightly beyond stop distance so they can still land hits without overlapping. */
function attackDistance(enemyRadius: number): number {
  return stopDistance(enemyRadius) + 0.35;
}

export interface EnemyEntity {
  id: string;
  kind: EnemyKind;
  name: string;
  health: number;
  maxHealth: number;
  damage: number;
  speed: number;
  aggroRange: number;
  attackCooldown: number;
  attackTimer: number;
  halfExtents: Vector3D;
  position: Vector3D;
  velocity: Vector3D;
  onGround: boolean;
  alive: boolean;
  /**
   * Seconds left in the death animation. >0 means corpse is still playing out
   * (alive is already false). 0 means living or fully removed.
   */
  deathTimer: number;
  /** Bats / Spire Mother hover; crawlers/grubs walk. */
  flying: boolean;
  mesh: Group;
  hitFlash: number;
  /** Seconds remaining where chase AI is suppressed (knockback window). */
  stunTimer: number;
  baseColor: number;
  baseEmissive: number;
  hpSprite: Sprite;
  hpCanvas: HTMLCanvasElement;
  hpCtx: CanvasRenderingContext2D;
  hpTexture: CanvasTexture;
  /** Boss AI phase index (kind-specific). */
  phase: number;
  telegraphTimer: number;
  specialTimer: number;
  invulnerable: boolean;
  burrowed: boolean;
  armorCracked: boolean;
  linkedBossId: string | null;
  /** Charge direction for Tunnel Horror (unit xz). */
  chargeDirX: number;
  chargeDirZ: number;
}

export interface EnemyUpdateResult {
  /** Damage dealt to the player this tick (0 if none). */
  playerDamage: number;
}

export interface EnemyRayHit {
  enemyId: string;
  distance: number;
}

export interface DamageResult {
  killed: boolean;
  name: string;
  wasBossClearTarget?: boolean;
}

/** Network-serializable enemy spawn payload (host → clients). */
export interface EnemyNetSpawn {
  id: string;
  kind: EnemyKind;
  name: string;
  health: number;
  maxHealth: number;
  damage: number;
  speed: number;
  aggroRange: number;
  attackCooldown: number;
  flying: boolean;
  position: Vector3D;
  phase?: number;
  invulnerable?: boolean;
  burrowed?: boolean;
  armorCracked?: boolean;
  linkedBossId?: string | null;
}

export interface EnemyNetState {
  id: string;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  health: number;
  alive: boolean;
  hitFlash?: boolean;
  phase?: number;
  invulnerable?: boolean;
  burrowed?: boolean;
  armorCracked?: boolean;
  telegraphTimer?: number;
}

export interface EnemyContactHit {
  targetId: string;
  amount: number;
  sourceEnemyId: string;
}

interface BossProjectile {
  mesh: Mesh;
  position: Vector3D;
  velocity: Vector3D;
  damage: number;
  life: number;
}

type TargetInfo = { id: string; position: Vector3D };

/**
 * Cave fauna + stage bosses: spawn per stage, chase / special AI, contact damage.
 * Collision reuses the same per-axis AABB sweep idea as the player.
 */
export class EnemyManager {
  private readonly enemies: EnemyEntity[] = [];
  private readonly scene: Scene;
  private readonly world: WorldManager;
  private readonly blockSize: number;
  private readonly bodyGeo = new BoxGeometry(1, 1, 1);
  private readonly headGeo = new SphereGeometry(0.35, 8, 6);
  private readonly coneGeo = new ConeGeometry(0.5, 1, 6);
  private readonly cylGeo = new CylinderGeometry(0.5, 0.5, 1, 8);
  private readonly flashOverlayGeo = new BoxGeometry(1.05, 1.05, 1.05);
  private readonly sporeGeo = new SphereGeometry(0.14, 6, 6);
  private readonly sporeMat = new MeshBasicMaterial({ color: 0xcc66ff });
  private readonly projectiles: BossProjectile[] = [];
  private nextId = 1;
  /** Shared clock for idle bob / wing flap / pulse. */
  private animTime = 0;

  constructor(scene: Scene, world: WorldManager, blockSize: number) {
    this.scene = scene;
    this.world = world;
    this.blockSize = blockSize;
  }

  list(): ReadonlyArray<EnemyEntity> {
    return this.enemies;
  }

  aliveCount(): number {
    let n = 0;
    for (const e of this.enemies) if (e.alive) n++;
    return n;
  }

  /** True when no boss-clear targets remain (or this is not a boss fight). */
  bossCleared(): boolean {
    for (const e of this.enemies) {
      if (e.alive && isBossClearTarget(e.kind)) return false;
    }
    return true;
  }

  /** Restore living bosses / totems to full HP after a party wipe. Returns true if anything changed. */
  healBossesToFull(): boolean {
    let changed = false;
    for (const e of this.enemies) {
      if (!e.alive || e.deathTimer > 0) continue;
      if (!isBossClearTarget(e.kind)) continue;

      if (
        e.health < e.maxHealth ||
        e.armorCracked ||
        e.burrowed ||
        e.phase !== 0 ||
        e.telegraphTimer > 0 ||
        e.hitFlash > 0
      ) {
        changed = true;
      }

      e.health = e.maxHealth;
      e.hitFlash = 0;
      e.stunTimer = 0;
      e.telegraphTimer = 0;
      e.attackTimer = 0.4;
      e.velocity.x = 0;
      e.velocity.z = 0;

      if (e.kind === EnemyKind.VaultSentinel) {
        e.phase = 0;
        e.armorCracked = false;
        e.specialTimer = SENTINEL_SLAM_INTERVAL * 0.6;
        e.invulnerable = false;
      } else if (e.kind === EnemyKind.SpireMother) {
        e.phase = 0;
        e.specialTimer = 0;
        e.invulnerable = true;
      } else if (e.kind === EnemyKind.TunnelHorror) {
        e.phase = 0;
        e.burrowed = false;
        e.specialTimer = HORROR_CYCLE_GAP;
        e.chargeDirX = 0;
        e.chargeDirZ = 0;
        e.invulnerable = false;
      } else if (e.kind === EnemyKind.SpireTotem) {
        e.phase = 0;
        e.invulnerable = false;
      }

      this.paintHpBar(e);
      this.syncMesh(e);
    }
    if (changed) this.clearProjectiles();
    return changed;
  }

  clearAll(): void {
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      this.destroyEnemy(this.enemies[i]!);
    }
    this.clearProjectiles();
  }

  /** Full spawn list for multiplayer sync. */
  exportSnapshot(): EnemyNetSpawn[] {
    return this.enemies
      .filter((e) => e.alive)
      .map((e) => ({
        id: e.id,
        kind: e.kind,
        name: e.name,
        health: e.health,
        maxHealth: e.maxHealth,
        damage: e.damage,
        speed: e.speed,
        aggroRange: e.aggroRange,
        attackCooldown: e.attackCooldown,
        flying: e.flying,
        position: { x: e.position.x, y: e.position.y, z: e.position.z },
        phase: e.phase,
        invulnerable: e.invulnerable,
        burrowed: e.burrowed,
        armorCracked: e.armorCracked,
        linkedBossId: e.linkedBossId,
      }));
  }

  exportState(): EnemyNetState[] {
    return this.enemies.map((e) => ({
      id: e.id,
      x: e.position.x,
      y: e.position.y,
      z: e.position.z,
      vx: e.velocity.x,
      vy: e.velocity.y,
      vz: e.velocity.z,
      health: e.health,
      alive: e.alive,
      hitFlash: e.hitFlash > 0,
      phase: e.phase,
      invulnerable: e.invulnerable,
      burrowed: e.burrowed,
      armorCracked: e.armorCracked,
      telegraphTimer: e.telegraphTimer,
    }));
  }

  /** Replace local fauna from a host snapshot (clients). */
  applySnapshot(spawns: ReadonlyArray<EnemyNetSpawn>): void {
    this.clearAll();
    for (const spawn of spawns) {
      this.spawnFromNetwork(spawn);
    }
  }

  /** Interpolate / snap positions from host state packets (clients). */
  applyNetworkState(states: ReadonlyArray<EnemyNetState>): void {
    const seen = new Set<string>();
    for (const s of states) {
      seen.add(s.id);
      const e = this.enemies.find((x) => x.id === s.id);
      if (!e) continue;
      if (!s.alive) {
        if (e.alive) this.beginDeath(e);
        continue;
      }
      if (e.deathTimer > 0) continue;
      e.position.x = s.x;
      e.position.y = s.y;
      e.position.z = s.z;
      e.velocity.x = s.vx;
      e.velocity.y = s.vy;
      e.velocity.z = s.vz;
      if (e.health !== s.health) {
        e.health = s.health;
        this.paintHpBar(e);
      }
      if (s.hitFlash) e.hitFlash = HIT_FLASH_DURATION;
      if (s.phase !== undefined) e.phase = s.phase;
      if (s.invulnerable !== undefined) e.invulnerable = s.invulnerable;
      if (s.burrowed !== undefined) e.burrowed = s.burrowed;
      if (s.armorCracked !== undefined) e.armorCracked = s.armorCracked;
      if (s.telegraphTimer !== undefined) e.telegraphTimer = s.telegraphTimer;
      this.syncMesh(e);
    }
    for (const e of [...this.enemies]) {
      if (seen.has(e.id)) continue;
      if (e.alive) this.beginDeath(e);
      // Dying corpses finish locally via updateDeath / updateVisuals.
    }
  }

  /** Client-side visual tick when host owns simulation. */
  updateVisuals(dt: number): void {
    this.animTime += dt;
    for (const e of [...this.enemies]) {
      if (e.deathTimer > 0) {
        this.updateDeath(e, dt);
        continue;
      }
      if (!e.alive) continue;
      if (e.hitFlash > 0) e.hitFlash = Math.max(0, e.hitFlash - dt);
      this.syncMesh(e);
    }
  }

  /**
   * Wipe previous stage fauna and place a fresh pack (or boss) in open air cells.
   * Spawns are scattered across the chamber with a minimum separation so packs
   * do not all wake and dogpile the player at the entrance.
   */
  spawnForStage(
    stage: number,
    level: LevelBounds,
    runId: number,
    worldSeed: number,
  ): number {
    this.clearAll();

    if (isBossStage(stage) || level.bossKind) {
      return this.spawnBossForStage(stage, level);
    }

    const count = enemyCountForStage(stage);
    const scale = stageScale(stage);
    const seed = (worldSeed ^ Math.imul(runId, 0x9e3779b9) ^ Math.imul(stage, 0x85ebca6b)) >>> 0;
    let spawned = 0;

    const floorY = level.floorY + 1;
    const ceilingY = level.ceilingY - 1;
    const placed: Array<{ x: number; z: number }> = [];
    const minSepSq = MIN_SPAWN_SEPARATION * MIN_SPAWN_SEPARATION;
    const maxAttempts = Math.max(count * 28, 40);

    for (let i = 0; i < maxAttempts && spawned < count; i++) {
      const h = hash3(i, stage, spawned, seed);
      const kind = pickEnemyKind(stage, hashToUnit(h));
      const def = ENEMY_CATALOG[kind];

      // Scatter across the full chamber footprint instead of the entrance→exit line.
      const vx = 2 + Math.floor(hashToUnit(hash2(i, 3, seed)) * Math.max(1, this.world.width - 4));
      const vz = 2 + Math.floor(hashToUnit(hash2(i, 4, seed)) * Math.max(1, this.world.depth - 4));

      let placedHere = false;
      for (let dy = 0; dy < 6 && !placedHere; dy++) {
        const roomH = Math.max(1, ceilingY - floorY - 1);
        const vy =
          kind === EnemyKind.Bat
            ? floorY + 2 + (hash2(i, dy, seed) % roomH)
            : floorY + (dy % 3);
        if (!this.world.inBounds(vx, vy, vz)) continue;
        if (this.world.isSolid(vx, vy, vz)) continue;
        if (kind !== EnemyKind.Bat && this.world.isSolid(vx, vy + 1, vz)) continue;
        // Prefer grounded walks on solid floor so crawlers are not floating in shafts.
        if (kind !== EnemyKind.Bat && !this.world.isSolid(vx, vy - 1, vz)) continue;

        const sx = (vx + 0.5) * this.blockSize;
        const sy = vy * this.blockSize + def.halfExtents.y;
        const sz = (vz + 0.5) * this.blockSize;
        const distEntrance = Math.hypot(sx - level.entrance.x, sz - level.entrance.z);
        if (distEntrance < 7.5) continue;

        const distExit = Math.hypot(
          sx - (level.exitDoor.x + 0.5) * this.blockSize,
          sz - (level.exitDoor.z + 0.5) * this.blockSize,
        );
        if (distExit < 4.5) continue;

        let tooClose = false;
        for (const p of placed) {
          const ddx = sx - p.x;
          const ddz = sz - p.z;
          if (ddx * ddx + ddz * ddz < minSepSq) {
            tooClose = true;
            break;
          }
        }
        if (tooClose) continue;

        const enemy = this.spawnEnemy(kind, { x: sx, y: sy, z: sz }, def, scale);
        if (enemy) {
          // Stagger first swings so a newly woken pack does not hit on the same frame.
          enemy.attackTimer = 0.35 + spawned * 0.4 + hashToUnit(hash2(i, 9, seed)) * 0.35;
        }
        placed.push({ x: sx, z: sz });
        spawned++;
        placedHere = true;
      }
    }

    return spawned;
  }

  private spawnBossForStage(stage: number, level: LevelBounds): number {
    const kind =
      (level.bossKind as EnemyKind | null) &&
      Object.values(EnemyKind).includes(level.bossKind as EnemyKind)
        ? (level.bossKind as EnemyKind)
        : bossKindForStage(stage);
    const scale = stageScale(stage);
    const def = ENEMY_CATALOG[kind];

    const cx = (level.exitDoor.x + Math.floor(level.entrance.x / this.blockSize)) / 2;
    const cz = (level.exitDoor.z + Math.floor(level.entrance.z / this.blockSize)) / 2;
    const floorY = level.floorY + 1;
    const sx = (Math.round(cx) + 0.5) * this.blockSize;
    const sz = (Math.round(cz) + 0.5) * this.blockSize;
    const sy = floorY * this.blockSize + def.halfExtents.y;

    const boss = this.spawnEnemy(kind, { x: sx, y: sy, z: sz }, def, scale, {
      flying: kind === EnemyKind.SpireMother,
      phase: 0,
      specialTimer: kind === EnemyKind.VaultSentinel ? SENTINEL_SLAM_INTERVAL * 0.6 : HORROR_CYCLE_GAP,
      invulnerable: kind === EnemyKind.SpireMother,
    });

    let count = 1;

    if (kind === EnemyKind.SpireMother && boss) {
      const totemDef = ENEMY_CATALOG[EnemyKind.SpireTotem];
      const pedestals = level.bossPedestals ?? [];
      for (let i = 0; i < pedestals.length; i++) {
        const p = pedestals[i]!;
        const tx = (p.x + 0.5) * this.blockSize;
        const ty = p.y * this.blockSize + totemDef.halfExtents.y;
        const tz = (p.z + 0.5) * this.blockSize;
        this.spawnEnemy(EnemyKind.SpireTotem, { x: tx, y: ty, z: tz }, totemDef, scale, {
          linkedBossId: boss.id,
          phase: 0,
        });
        count++;
      }
      // Fallback if pedestals missing from older snapshots.
      if (pedestals.length === 0) {
        for (const [ox, oz] of [
          [-4, -2],
          [4, -2],
          [0, 4],
        ] as const) {
          this.spawnEnemy(
            EnemyKind.SpireTotem,
            {
              x: sx + ox * this.blockSize,
              y: sy,
              z: sz + oz * this.blockSize,
            },
            totemDef,
            scale,
            { linkedBossId: boss.id },
          );
          count++;
        }
      }
    }

    return count;
  }

  /**
   * Chase AI + contact damage (+ boss specials).
   */
  update(
    dt: number,
    playerPos: Vector3D,
    playerAlive: boolean,
    options?: {
      localPlayerId?: string;
      extraTargets?: ReadonlyArray<{ id: string; position: Vector3D; alive: boolean }>;
    },
  ): EnemyUpdateResult & { contactHits: EnemyContactHit[] } {
    const contactHits: EnemyContactHit[] = [];
    let playerDamage = 0;

    const targets: TargetInfo[] = [];
    if (playerAlive) {
      targets.push({ id: options?.localPlayerId ?? 'local', position: playerPos });
    }
    if (options?.extraTargets) {
      for (const t of options.extraTargets) {
        if (t.alive) targets.push({ id: t.id, position: t.position });
      }
    }

    const localId = options?.localPlayerId ?? 'local';

    this.animTime += dt;

    for (const e of [...this.enemies]) {
      if (e.deathTimer > 0) this.updateDeath(e, dt);
    }

    if (targets.length === 0) {
      for (const e of this.enemies) {
        if (!e.alive) continue;
        e.velocity.x *= 0.9;
        e.velocity.z *= 0.9;
        this.integrateEnemy(e, dt);
      }
      this.separateEnemies();
      for (const e of this.enemies) {
        if (e.alive) this.syncMesh(e);
      }
      this.clearProjectiles();
      return { playerDamage: 0, contactHits };
    }

    for (const e of this.enemies) {
      if (!e.alive) continue;

      if (e.hitFlash > 0) {
        e.hitFlash = Math.max(0, e.hitFlash - dt);
      }
      if (e.stunTimer > 0) {
        e.stunTimer = Math.max(0, e.stunTimer - dt);
      }
      if (e.attackTimer > 0) {
        e.attackTimer = Math.max(0, e.attackTimer - dt);
      }
    }

    // Assign chase slots so only a few fauna swarm at once; closest (and
    // anyone already in melee range) get priority.
    const chaseAllowed = this.assignFaunaChaseSlots(targets);

    for (const e of this.enemies) {
      if (!e.alive) continue;

      let best: { id: string; position: Vector3D; dist: number; horiz: number } | null = null;
      for (const t of targets) {
        const dx = t.position.x - e.position.x;
        const dy = t.position.y - e.position.y;
        const dz = t.position.z - e.position.z;
        const horiz = Math.hypot(dx, dz);
        const dist = Math.hypot(dx, dy, dz);
        if (!best || horiz < best.horiz) best = { id: t.id, position: t.position, dist, horiz };
      }
      if (!best) continue;

      if (e.kind === EnemyKind.SpireTotem) {
        e.velocity.x = 0;
        e.velocity.z = 0;
        e.velocity.y = 0;
        continue;
      }

      if (e.kind === EnemyKind.VaultSentinel) {
        const dmg = this.updateVaultSentinel(e, dt, best, targets, localId, contactHits);
        playerDamage += dmg;
        continue;
      }

      if (e.kind === EnemyKind.SpireMother) {
        const dmg = this.updateSpireMother(e, dt, best, targets, localId, contactHits);
        playerDamage += dmg;
        continue;
      }

      if (e.kind === EnemyKind.TunnelHorror) {
        const dmg = this.updateTunnelHorror(e, dt, best, targets, localId, contactHits);
        playerDamage += dmg;
        continue;
      }

      this.updateFaunaChase(
        e,
        dt,
        best,
        targets,
        localId,
        contactHits,
        chaseAllowed.has(e.id),
        (n) => {
          playerDamage += n;
        },
      );
    }

    this.separateEnemies();
    for (const e of this.enemies) {
      if (e.alive) this.syncMesh(e);
    }

    playerDamage += this.updateBossProjectiles(dt, targets, localId, contactHits);

    return { playerDamage, contactHits };
  }

  /**
   * Pick which fauna may chase this frame. Closest enemies fill the slots;
   * anyone already inside IMMEDIATE_THREAT_RANGE always qualifies.
   */
  private assignFaunaChaseSlots(targets: ReadonlyArray<TargetInfo>): Set<string> {
    const allowed = new Set<string>();
    if (targets.length === 0) return allowed;

    type Candidate = { id: string; horiz: number; aggro: number };
    const candidates: Candidate[] = [];

    for (const e of this.enemies) {
      if (!e.alive || e.deathTimer > 0 || e.burrowed) continue;
      if (
        e.kind === EnemyKind.VaultSentinel ||
        e.kind === EnemyKind.SpireMother ||
        e.kind === EnemyKind.SpireTotem ||
        e.kind === EnemyKind.TunnelHorror
      ) {
        continue;
      }

      let bestHoriz = Infinity;
      for (const t of targets) {
        const horiz = Math.hypot(t.position.x - e.position.x, t.position.z - e.position.z);
        if (horiz < bestHoriz) bestHoriz = horiz;
      }
      if (!Number.isFinite(bestHoriz)) continue;
      if (bestHoriz > e.aggroRange && bestHoriz > IMMEDIATE_THREAT_RANGE) continue;
      candidates.push({ id: e.id, horiz: bestHoriz, aggro: e.aggroRange });
    }

    candidates.sort((a, b) => a.horiz - b.horiz);

    for (const c of candidates) {
      if (c.horiz <= IMMEDIATE_THREAT_RANGE) allowed.add(c.id);
    }
    for (const c of candidates) {
      if (allowed.size >= MAX_ACTIVE_CHASERS) break;
      if (c.horiz <= c.aggro) allowed.add(c.id);
    }

    return allowed;
  }

  private updateFaunaChase(
    e: EnemyEntity,
    dt: number,
    best: { id: string; position: Vector3D; dist: number; horiz: number },
    targets: TargetInfo[],
    localId: string,
    contactHits: EnemyContactHit[],
    allowChase: boolean,
    addLocalDamage: (n: number) => void,
  ): void {
    const dx = best.position.x - e.position.x;
    const dz = best.position.z - e.position.z;
    const horiz = best.horiz;
    const stopAt = stopDistance(e.halfExtents.x);
    const attackAt = attackDistance(e.halfExtents.x);
    const canChase = allowChase || horiz <= IMMEDIATE_THREAT_RANGE;

    if (e.stunTimer > 0) {
      e.velocity.x *= Math.max(0, 1 - 2.5 * dt);
      e.velocity.z *= Math.max(0, 1 - 2.5 * dt);
    } else if (canChase && horiz < e.aggroRange && horiz > stopAt) {
      const inv = 1 / Math.max(horiz, EPSILON);
      const nx = dx * inv;
      const nz = dz * inv;
      const approach = Math.min(1, (horiz - stopAt) / 1.2);
      e.velocity.x = nx * e.speed * approach;
      e.velocity.z = nz * e.speed * approach;

      if (e.flying) {
        const targetY = best.position.y + 0.55;
        e.velocity.y = Math.max(-e.speed, Math.min(e.speed, (targetY - e.position.y) * 2.2));
      }
    } else {
      // Idle / waiting for a chase slot — damp motion instead of stacking on the player.
      e.velocity.x *= Math.max(0, 1 - 8 * dt);
      e.velocity.z *= Math.max(0, 1 - 8 * dt);
      if (e.flying) {
        const hoverY = canChase ? best.position.y + 0.55 : e.position.y;
        e.velocity.y = Math.max(-e.speed, Math.min(e.speed, (hoverY - e.position.y) * 1.2));
      }
    }

    if (!e.flying) {
      e.velocity.y += -28 * dt;
      if (e.velocity.y < -42) e.velocity.y = -42;
      if (e.stunTimer <= 0 && canChase && horiz < e.aggroRange && horiz > stopAt) {
        this.tryGroundJump(e, best.position);
      }
    }

    this.integrateEnemy(e, dt);
    this.separateFromPlayers(e, targets);

    // Only enemies that are actively engaged (or already on top of you) may hit.
    if (canChase && e.stunTimer <= 0 && e.attackTimer <= 0) {
      for (const t of targets) {
        const touchHoriz = Math.hypot(t.position.x - e.position.x, t.position.z - e.position.z);
        const touchY = Math.abs(t.position.y - e.position.y);
        if (touchHoriz <= attackAt && touchY <= 1.6) {
          contactHits.push({
            targetId: t.id,
            amount: e.damage,
            sourceEnemyId: e.id,
          });
          if (t.id === localId) addLocalDamage(e.damage);
          e.attackTimer = e.attackCooldown;
          break;
        }
      }
    }
  }

  /** Phase 0 chase, 1 telegraph, 2 cracked window. */
  private updateVaultSentinel(
    e: EnemyEntity,
    dt: number,
    best: { id: string; position: Vector3D; dist: number; horiz: number },
    targets: TargetInfo[],
    localId: string,
    contactHits: EnemyContactHit[],
  ): number {
    let playerDamage = 0;
    e.invulnerable = false;

    if (e.phase === 1) {
      // Telegraph slam — freeze in place.
      e.velocity.x = 0;
      e.velocity.z = 0;
      e.velocity.y += -28 * dt;
      e.telegraphTimer -= dt;
      e.hitFlash = Math.max(e.hitFlash, 0.08);
      this.integrateEnemy(e, dt);
      if (e.telegraphTimer <= 0) {
        // Resolve slam AoE.
        for (const t of targets) {
          const horiz = Math.hypot(t.position.x - e.position.x, t.position.z - e.position.z);
          const touchY = Math.abs(t.position.y - e.position.y);
          if (horiz <= SENTINEL_SLAM_RADIUS && touchY <= 2.2) {
            const amount = Math.round(e.damage * 1.4);
            contactHits.push({ targetId: t.id, amount, sourceEnemyId: e.id });
            if (t.id === localId) playerDamage += amount;
          }
        }
        e.phase = 2;
        e.armorCracked = true;
        e.specialTimer = SENTINEL_CRACK_DURATION;
        e.telegraphTimer = 0;
      }
      return playerDamage;
    }

    if (e.phase === 2) {
      e.armorCracked = true;
      e.specialTimer -= dt;
      // Slow chase while cracked.
      this.chaseToward(e, best, dt, e.speed * 0.55, true);
      this.integrateEnemy(e, dt);
      this.separateFromPlayers(e, targets);
      playerDamage += this.tryContact(e, targets, localId, contactHits, attackDistance(e.halfExtents.x));
      if (e.specialTimer <= 0) {
        e.phase = 0;
        e.armorCracked = false;
        e.specialTimer = SENTINEL_SLAM_INTERVAL;
      }
      return playerDamage;
    }

    // Phase 0 — armored chase, wind up slam.
    e.armorCracked = false;
    e.specialTimer -= dt;
    this.chaseToward(e, best, dt, e.speed, true);
    this.integrateEnemy(e, dt);
    this.separateFromPlayers(e, targets);
    playerDamage += this.tryContact(e, targets, localId, contactHits, attackDistance(e.halfExtents.x));

    if (e.specialTimer <= 0 && e.stunTimer <= 0 && best.horiz < e.aggroRange) {
      e.phase = 1;
      e.telegraphTimer = SENTINEL_TELEGRAPH;
      e.velocity.x = 0;
      e.velocity.z = 0;
    }
    return playerDamage;
  }

  private updateSpireMother(
    e: EnemyEntity,
    dt: number,
    best: { id: string; position: Vector3D; dist: number; horiz: number },
    targets: TargetInfo[],
    localId: string,
    contactHits: EnemyContactHit[],
  ): number {
    let playerDamage = 0;
    const totemsAlive = this.enemies.some(
      (t) => t.alive && t.kind === EnemyKind.SpireTotem && t.linkedBossId === e.id,
    );
    e.invulnerable = totemsAlive;
    e.flying = true;

    if (totemsAlive) {
      // Strafe / hover and fire spores.
      const orbit = 0.9;
      const angle = performance.now() * 0.0012 + e.phase;
      const tx = best.position.x + Math.cos(angle) * 5;
      const tz = best.position.z + Math.sin(angle) * 5;
      const dx = tx - e.position.x;
      const dz = tz - e.position.z;
      const horiz = Math.hypot(dx, dz);
      if (horiz > 0.4) {
        const inv = 1 / horiz;
        e.velocity.x = dx * inv * e.speed * orbit;
        e.velocity.z = dz * inv * e.speed * orbit;
      } else {
        e.velocity.x *= 0.85;
        e.velocity.z *= 0.85;
      }
      const targetY = best.position.y + 1.4;
      e.velocity.y = Math.max(-e.speed, Math.min(e.speed, (targetY - e.position.y) * 2));
      this.integrateEnemy(e, dt);

      e.specialTimer -= dt;
      if (e.specialTimer <= 0) {
        this.spawnSpore(e, best.position);
        e.specialTimer = SPIRE_SPORE_INTERVAL;
      }
      return playerDamage;
    }

    // Totems down — aggressive chase.
    e.phase = 1;
    this.chaseToward(e, best, dt, e.speed * 1.35, false);
    const targetY = best.position.y + 0.4;
    e.velocity.y = Math.max(-e.speed, Math.min(e.speed, (targetY - e.position.y) * 2.4));
    this.integrateEnemy(e, dt);
    this.separateFromPlayers(e, targets);
    playerDamage += this.tryContact(
      e,
      targets,
      localId,
      contactHits,
      attackDistance(e.halfExtents.x) + 0.2,
    );
    return playerDamage;
  }

  /**
   * Tunnel Horror phases:
   * 0 wait → 1 burrow move → 2 emerge telegraph → 3 charge → (wall/hit) stun via stunTimer then 0
   */
  private updateTunnelHorror(
    e: EnemyEntity,
    dt: number,
    best: { id: string; position: Vector3D; dist: number; horiz: number },
    targets: TargetInfo[],
    localId: string,
    contactHits: EnemyContactHit[],
  ): number {
    let playerDamage = 0;
    e.flying = false;
    e.invulnerable = e.burrowed;

    if (e.stunTimer > 0) {
      e.burrowed = false;
      e.phase = 0;
      e.velocity.x *= Math.max(0, 1 - 4 * dt);
      e.velocity.z *= Math.max(0, 1 - 4 * dt);
      e.velocity.y += -28 * dt;
      this.integrateEnemy(e, dt);
      return playerDamage;
    }

    if (e.phase === 0) {
      e.burrowed = false;
      e.specialTimer -= dt;
      e.velocity.x *= 0.9;
      e.velocity.z *= 0.9;
      e.velocity.y += -28 * dt;
      this.integrateEnemy(e, dt);
      if (e.specialTimer <= 0 && best.horiz < e.aggroRange) {
        e.phase = 1;
        e.burrowed = true;
        e.specialTimer = HORROR_BURROW_TIME;
        e.mesh.visible = true;
      }
      return playerDamage;
    }

    if (e.phase === 1) {
      // Burrowed — move under player, untargetable.
      e.burrowed = true;
      e.specialTimer -= dt;
      const dx = best.position.x - e.position.x;
      const dz = best.position.z - e.position.z;
      const horiz = Math.hypot(dx, dz);
      if (horiz > 0.3) {
        const inv = 1 / horiz;
        e.velocity.x = dx * inv * e.speed * 1.6;
        e.velocity.z = dz * inv * e.speed * 1.6;
      }
      e.velocity.y = 0;
      this.integrateEnemy(e, dt);
      if (e.specialTimer <= 0 || horiz < 1.2) {
        e.phase = 2;
        e.burrowed = false;
        e.telegraphTimer = HORROR_EMERGE_TELEGRAPH;
        e.velocity.x = 0;
        e.velocity.z = 0;
        const slot = this.world.levelAtWorldY(e.position.y) || 1;
        const floorY = this.world.getLevel(slot)?.floorY ?? Math.floor(e.position.y / this.blockSize) - 1;
        e.position.y = (floorY + 1) * this.blockSize + e.halfExtents.y;
      }
      return playerDamage;
    }

    if (e.phase === 2) {
      e.burrowed = false;
      e.telegraphTimer -= dt;
      e.hitFlash = Math.max(e.hitFlash, 0.1);
      e.velocity.x = 0;
      e.velocity.z = 0;
      e.velocity.y += -28 * dt;
      this.integrateEnemy(e, dt);
      if (e.telegraphTimer <= 0) {
        const dx = best.position.x - e.position.x;
        const dz = best.position.z - e.position.z;
        const horiz = Math.hypot(dx, dz);
        const inv = 1 / Math.max(horiz, EPSILON);
        e.chargeDirX = dx * inv;
        e.chargeDirZ = dz * inv;
        e.phase = 3;
        e.specialTimer = 1.4;
      }
      return playerDamage;
    }

    // Phase 3 — charge.
    e.burrowed = false;
    e.specialTimer -= dt;
    e.velocity.x = e.chargeDirX * HORROR_CHARGE_SPEED;
    e.velocity.z = e.chargeDirZ * HORROR_CHARGE_SPEED;
    e.velocity.y += -28 * dt;
    const prevX = e.position.x;
    const prevZ = e.position.z;
    this.integrateEnemy(e, dt);

    // Wall smash: velocity killed on X/Z while still charging.
    const blocked =
      (Math.abs(e.velocity.x) < 0.5 && Math.abs(e.chargeDirX) > 0.3) ||
      (Math.abs(e.velocity.z) < 0.5 && Math.abs(e.chargeDirZ) > 0.3);
    const moved = Math.hypot(e.position.x - prevX, e.position.z - prevZ);

    if (blocked && moved < HORROR_CHARGE_SPEED * dt * 0.35) {
      e.stunTimer = HORROR_WALL_STUN;
      e.phase = 0;
      e.specialTimer = HORROR_CYCLE_GAP;
      e.velocity.x = 0;
      e.velocity.z = 0;
      e.hitFlash = HIT_FLASH_DURATION;
      return playerDamage;
    }

    // Player hit during charge.
    for (const t of targets) {
      const touchHoriz = Math.hypot(t.position.x - e.position.x, t.position.z - e.position.z);
      const touchY = Math.abs(t.position.y - e.position.y);
      if (touchHoriz <= attackDistance(e.halfExtents.x) + 0.5 && touchY <= 1.8) {
        const amount = Math.round(e.damage * HORROR_CHARGE_DAMAGE_SCALE);
        contactHits.push({ targetId: t.id, amount, sourceEnemyId: e.id });
        if (t.id === localId) playerDamage += amount;
        e.stunTimer = HORROR_HIT_STUN;
        e.phase = 0;
        e.specialTimer = HORROR_CYCLE_GAP;
        e.velocity.x = 0;
        e.velocity.z = 0;
        break;
      }
    }

    if (e.specialTimer <= 0 && e.phase === 3) {
      e.phase = 0;
      e.specialTimer = HORROR_CYCLE_GAP;
      e.velocity.x = 0;
      e.velocity.z = 0;
    }

    return playerDamage;
  }

  private chaseToward(
    e: EnemyEntity,
    best: { position: Vector3D; horiz: number },
    dt: number,
    speed: number,
    grounded: boolean,
  ): void {
    const dx = best.position.x - e.position.x;
    const dz = best.position.z - e.position.z;
    const horiz = best.horiz;
    const stopAt = stopDistance(e.halfExtents.x);
    if (horiz > stopAt) {
      const inv = 1 / Math.max(horiz, EPSILON);
      const approach = Math.min(1, (horiz - stopAt) / 1.2);
      e.velocity.x = dx * inv * speed * approach;
      e.velocity.z = dz * inv * speed * approach;
    } else {
      e.velocity.x *= Math.max(0, 1 - 8 * dt);
      e.velocity.z *= Math.max(0, 1 - 8 * dt);
    }
    if (grounded) {
      e.velocity.y += -28 * dt;
      if (e.velocity.y < -42) e.velocity.y = -42;
      if (horiz > stopAt) this.tryGroundJump(e, best.position);
    }
  }

  /**
   * Jump when the target is above or a 1-block ledge sits in the chase path.
   * Call after gravity so the impulse is not eroded on the same tick.
   */
  private tryGroundJump(e: EnemyEntity, target: Vector3D): void {
    if (e.flying || !e.onGround || e.burrowed) return;

    const targetFeet = target.y - PLAYER_HALF_HEIGHT;
    const myFeet = e.position.y - e.halfExtents.y;
    const wantUp = targetFeet - myFeet >= ENEMY_JUMP_HEIGHT_THRESHOLD;
    const blocked = this.hasClimbableLedgeAhead(e);
    if (!wantUp && !blocked) return;

    e.velocity.y = ENEMY_JUMP_VELOCITY;
    e.onGround = false;
  }

  /** True if a single solid step sits ahead in the current horizontal move direction. */
  private hasClimbableLedgeAhead(e: EnemyEntity): boolean {
    const hx = e.velocity.x;
    const hz = e.velocity.z;
    const hLen = Math.hypot(hx, hz);
    if (hLen < 0.15) return false;

    const inv = 1 / hLen;
    const look = Math.max(e.halfExtents.x, e.halfExtents.z) + 0.4;
    const px = e.position.x + hx * inv * look;
    const pz = e.position.z + hz * inv * look;
    const bs = this.blockSize;
    const vx = Math.floor(px / bs);
    const vz = Math.floor(pz / bs);
    const footY = Math.floor((e.position.y - e.halfExtents.y + 0.15) / bs);
    const bodyY = Math.floor(e.position.y / bs);

    const solidFoot = this.world.isSolid(vx, footY, vz);
    const solidBody = bodyY !== footY && this.world.isSolid(vx, bodyY, vz);
    if (!solidFoot && !solidBody) return false;

    // Only jump onto / over a 1-block step — refuse taller walls.
    const stepTop = (solidFoot ? footY : bodyY) + 1;
    return !this.world.isSolid(vx, stepTop, vz);
  }

  private tryContact(
    e: EnemyEntity,
    targets: TargetInfo[],
    localId: string,
    contactHits: EnemyContactHit[],
    attackAt: number,
  ): number {
    if (e.stunTimer > 0 || e.attackTimer > 0 || e.burrowed) return 0;
    let dmg = 0;
    for (const t of targets) {
      const touchHoriz = Math.hypot(t.position.x - e.position.x, t.position.z - e.position.z);
      const touchY = Math.abs(t.position.y - e.position.y);
      if (touchHoriz <= attackAt && touchY <= 1.8) {
        contactHits.push({
          targetId: t.id,
          amount: e.damage,
          sourceEnemyId: e.id,
        });
        if (t.id === localId) dmg += e.damage;
        e.attackTimer = e.attackCooldown;
        break;
      }
    }
    return dmg;
  }

  private spawnSpore(from: EnemyEntity, target: Vector3D): void {
    const dx = target.x - from.position.x;
    const dy = target.y - from.position.y;
    const dz = target.z - from.position.z;
    const len = Math.hypot(dx, dy, dz) || 1;
    const mesh = new Mesh(this.sporeGeo, this.sporeMat.clone());
    mesh.position.set(from.position.x, from.position.y + 0.4, from.position.z);
    this.scene.add(mesh);
    this.projectiles.push({
      mesh,
      position: {
        x: from.position.x,
        y: from.position.y + 0.4,
        z: from.position.z,
      },
      velocity: {
        x: (dx / len) * SPIRE_SPORE_SPEED,
        y: (dy / len) * SPIRE_SPORE_SPEED,
        z: (dz / len) * SPIRE_SPORE_SPEED,
      },
      damage: Math.max(8, Math.round(from.damage * SPIRE_SPORE_DAMAGE_SCALE)),
      life: SPIRE_SPORE_LIFE,
    });
  }

  private updateBossProjectiles(
    dt: number,
    targets: TargetInfo[],
    localId: string,
    contactHits: EnemyContactHit[],
  ): number {
    let playerDamage = 0;
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i]!;
      p.life -= dt;
      p.position.x += p.velocity.x * dt;
      p.position.y += p.velocity.y * dt;
      p.position.z += p.velocity.z * dt;
      p.mesh.position.set(p.position.x, p.position.y, p.position.z);

      const vx = Math.floor(p.position.x / this.blockSize);
      const vy = Math.floor(p.position.y / this.blockSize);
      const vz = Math.floor(p.position.z / this.blockSize);
      if (p.life <= 0 || this.world.isSolid(vx, vy, vz)) {
        this.destroyProjectile(i);
        continue;
      }

      let hit = false;
      for (const t of targets) {
        const dx = t.position.x - p.position.x;
        const dy = t.position.y - p.position.y;
        const dz = t.position.z - p.position.z;
        if (Math.hypot(dx, dy, dz) < 0.7) {
          contactHits.push({
            targetId: t.id,
            amount: p.damage,
            sourceEnemyId: 'spore',
          });
          if (t.id === localId) playerDamage += p.damage;
          hit = true;
          break;
        }
      }
      if (hit) this.destroyProjectile(i);
    }
    return playerDamage;
  }

  private destroyProjectile(index: number): void {
    const p = this.projectiles[index];
    if (!p) return;
    (p.mesh.material as MeshBasicMaterial).dispose();
    p.mesh.removeFromParent();
    this.projectiles.splice(index, 1);
  }

  private clearProjectiles(): void {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      this.destroyProjectile(i);
    }
  }

  /**
   * Apply weapon damage + knockback away from the hit origin.
   */
  applyDamage(
    enemyId: string,
    amount: number,
    knockDir?: Vector3D | null,
  ): DamageResult | null {
    const e = this.enemies.find((x) => x.id === enemyId);
    if (!e || !e.alive) return null;
    if (e.invulnerable || e.burrowed) {
      e.hitFlash = HIT_FLASH_DURATION * 0.5;
      this.syncMesh(e);
      return { killed: false, name: e.name };
    }

    let dealt = amount;
    if (e.kind === EnemyKind.VaultSentinel && !e.armorCracked) {
      dealt = Math.max(1, Math.round(amount * SENTINEL_ARMOR_MULT));
    }

    e.health -= dealt;
    e.hitFlash = HIT_FLASH_DURATION;
    if (!isBossKind(e.kind) && e.kind !== EnemyKind.SpireTotem) {
      e.stunTimer = STUN_DURATION;
    }
    this.paintHpBar(e);

    if (knockDir && !isBossKind(e.kind) && e.kind !== EnemyKind.SpireTotem) {
      const len = Math.hypot(knockDir.x, knockDir.y, knockDir.z);
      if (len > 1e-6) {
        const inv = 1 / len;
        e.velocity.x = knockDir.x * inv * KNOCKBACK_SPEED;
        e.velocity.z = knockDir.z * inv * KNOCKBACK_SPEED;
        e.velocity.y = Math.max(e.velocity.y, knockDir.y * inv * KNOCKBACK_UP + 2.2);
        e.onGround = false;
      }
    }

    const name = e.name;
    const wasBossClearTarget = isBossClearTarget(e.kind);
    if (e.health <= 0) {
      e.health = 0;
      this.beginDeath(e, knockDir);
      return { killed: true, name, wasBossClearTarget };
    }

    this.syncMesh(e);
    return { killed: false, name, wasBossClearTarget };
  }

  /** Start tip-over death sequence; corpse is removed when the timer elapses. */
  private beginDeath(e: EnemyEntity, knockDir?: Vector3D | null): void {
    if (e.deathTimer > 0) return;
    e.alive = false;
    e.health = 0;
    e.deathTimer = isBossKind(e.kind) ? BOSS_DEATH_ANIM_DURATION : DEATH_ANIM_DURATION;
    e.hitFlash = HIT_FLASH_DURATION;
    e.stunTimer = 0;
    e.telegraphTimer = 0;
    e.invulnerable = false;
    e.burrowed = false;
    e.hpSprite.visible = false;
    e.onGround = false;

    if (knockDir) {
      const len = Math.hypot(knockDir.x, knockDir.y, knockDir.z);
      if (len > 1e-6) {
        const inv = 1 / len;
        e.velocity.x = knockDir.x * inv * (KNOCKBACK_SPEED * 0.85);
        e.velocity.z = knockDir.z * inv * (KNOCKBACK_SPEED * 0.85);
        e.velocity.y = Math.max(e.velocity.y, knockDir.y * inv * KNOCKBACK_UP + 3.5);
      }
    } else {
      e.velocity.x *= 0.55;
      e.velocity.z *= 0.55;
      e.velocity.y = Math.max(e.velocity.y, e.flying ? 2.8 : 4.2);
    }

    const side =
      Math.abs(e.velocity.x) > Math.abs(e.velocity.z)
        ? Math.sign(e.velocity.x) || 1
        : Math.sign(e.velocity.z) || 1;
    e.mesh.userData.deathSide = side;
    e.mesh.userData.deathYaw = e.mesh.rotation.y;
    e.mesh.userData.deathDuration = e.deathTimer;
  }

  private updateDeath(e: EnemyEntity, dt: number): void {
    e.deathTimer = Math.max(0, e.deathTimer - dt);
    if (e.hitFlash > 0) e.hitFlash = Math.max(0, e.hitFlash - dt);

    e.velocity.x *= Math.max(0, 1 - 2.8 * dt);
    e.velocity.z *= Math.max(0, 1 - 2.8 * dt);
    if (!e.flying || e.deathTimer < (e.mesh.userData.deathDuration as number) * 0.55) {
      e.velocity.y += -28 * dt;
      if (e.velocity.y < -42) e.velocity.y = -42;
    } else {
      e.velocity.y *= Math.max(0, 1 - 1.5 * dt);
    }
    this.integrateEnemy(e, dt);
    this.syncMesh(e);

    if (e.deathTimer <= 0) {
      this.destroyEnemy(e);
    }
  }

  /** Closest living enemy AABB along a ray, or null. */
  raycast(origin: Vector3D, dir: Vector3D, maxDistance: number): EnemyRayHit | null {
    const len = Math.hypot(dir.x, dir.y, dir.z);
    if (len < 1e-8) return null;
    const dx = dir.x / len;
    const dy = dir.y / len;
    const dz = dir.z / len;

    let best: EnemyRayHit | null = null;

    for (const e of this.enemies) {
      if (!e.alive || e.burrowed) continue;
      const t = rayAabbDistance(
        origin,
        { x: dx, y: dy, z: dz },
        {
          x: e.position.x - e.halfExtents.x,
          y: e.position.y - e.halfExtents.y,
          z: e.position.z - e.halfExtents.z,
        },
        {
          x: e.position.x + e.halfExtents.x,
          y: e.position.y + e.halfExtents.y,
          z: e.position.z + e.halfExtents.z,
        },
        maxDistance,
      );
      if (t === null) continue;
      if (!best || t < best.distance) {
        best = { enemyId: e.id, distance: t };
      }
    }
    return best;
  }

  /** First living enemy whose AABB contains the point (for projectiles). */
  hitTestPoint(point: Vector3D): string | null {
    for (const e of this.enemies) {
      if (!e.alive || e.burrowed) continue;
      if (
        point.x >= e.position.x - e.halfExtents.x &&
        point.x <= e.position.x + e.halfExtents.x &&
        point.y >= e.position.y - e.halfExtents.y &&
        point.y <= e.position.y + e.halfExtents.y &&
        point.z >= e.position.z - e.halfExtents.z &&
        point.z <= e.position.z + e.halfExtents.z
      ) {
        return e.id;
      }
    }
    return null;
  }

  dispose(): void {
    this.clearAll();
    this.bodyGeo.dispose();
    this.headGeo.dispose();
    this.coneGeo.dispose();
    this.cylGeo.dispose();
    this.flashOverlayGeo.dispose();
    this.sporeGeo.dispose();
    this.sporeMat.dispose();
  }

  private spawnEnemy(
    kind: EnemyKind,
    position: Vector3D,
    def: EnemyDef,
    scale: { hp: number; damage: number },
    extras?: Partial<{
      flying: boolean;
      phase: number;
      specialTimer: number;
      invulnerable: boolean;
      linkedBossId: string | null;
    }>,
  ): EnemyEntity {
    return this.createEnemyEntity({
      id: `enemy-${this.nextId++}`,
      kind,
      name: def.name,
      health: Math.round(def.maxHealth * scale.hp),
      maxHealth: Math.round(def.maxHealth * scale.hp),
      damage: Math.round(def.damage * scale.damage),
      speed: def.speed,
      aggroRange: def.aggroRange,
      attackCooldown: def.attackCooldown,
      flying: extras?.flying ?? kind === EnemyKind.Bat,
      position,
      def,
      phase: extras?.phase ?? 0,
      specialTimer: extras?.specialTimer ?? 0,
      invulnerable: extras?.invulnerable ?? false,
      linkedBossId: extras?.linkedBossId ?? null,
    });
  }

  private spawnFromNetwork(spawn: EnemyNetSpawn): void {
    const kind = (Object.values(EnemyKind) as string[]).includes(spawn.kind)
      ? (spawn.kind as EnemyKind)
      : EnemyKind.Crawler;
    const def = ENEMY_CATALOG[kind];
    this.createEnemyEntity({
      id: spawn.id,
      kind,
      name: spawn.name || def.name,
      health: spawn.health,
      maxHealth: spawn.maxHealth || def.maxHealth,
      damage: spawn.damage || def.damage,
      speed: spawn.speed || def.speed,
      aggroRange: spawn.aggroRange || def.aggroRange,
      attackCooldown: spawn.attackCooldown || def.attackCooldown,
      flying: spawn.flying ?? (kind === EnemyKind.Bat || kind === EnemyKind.SpireMother),
      position: spawn.position,
      def,
      phase: spawn.phase ?? 0,
      specialTimer: 0,
      invulnerable: spawn.invulnerable ?? false,
      linkedBossId: spawn.linkedBossId ?? null,
      burrowed: spawn.burrowed ?? false,
      armorCracked: spawn.armorCracked ?? false,
    });
    const numeric = Number(spawn.id.replace(/\D/g, ''));
    if (Number.isFinite(numeric) && numeric >= this.nextId) {
      this.nextId = numeric + 1;
    }
  }

  private createEnemyEntity(opts: {
    id: string;
    kind: EnemyKind;
    name: string;
    health: number;
    maxHealth: number;
    damage: number;
    speed: number;
    aggroRange: number;
    attackCooldown: number;
    flying: boolean;
    position: Vector3D;
    def: EnemyDef;
    phase?: number;
    specialTimer?: number;
    invulnerable?: boolean;
    linkedBossId?: string | null;
    burrowed?: boolean;
    armorCracked?: boolean;
  }): EnemyEntity {
    const { mesh, hpSprite, hpCanvas, hpCtx, hpTexture } = this.createMesh(opts.def);
    mesh.position.set(opts.position.x, opts.position.y, opts.position.z);
    this.scene.add(mesh);

    const enemy: EnemyEntity = {
      id: opts.id,
      kind: opts.kind,
      name: opts.name,
      health: opts.health,
      maxHealth: opts.maxHealth,
      damage: opts.damage,
      speed: opts.speed,
      aggroRange: opts.aggroRange,
      attackCooldown: opts.attackCooldown,
      attackTimer: 0.4,
      halfExtents: { ...opts.def.halfExtents },
      position: { ...opts.position },
      velocity: { x: 0, y: 0, z: 0 },
      onGround: false,
      alive: true,
      deathTimer: 0,
      flying: opts.flying,
      mesh,
      hitFlash: 0,
      stunTimer: 0,
      baseColor: opts.def.color,
      baseEmissive: opts.def.emissive,
      hpSprite,
      hpCanvas,
      hpCtx,
      hpTexture,
      phase: opts.phase ?? 0,
      telegraphTimer: 0,
      specialTimer: opts.specialTimer ?? 0,
      invulnerable: opts.invulnerable ?? false,
      burrowed: opts.burrowed ?? false,
      armorCracked: opts.armorCracked ?? false,
      linkedBossId: opts.linkedBossId ?? null,
      chargeDirX: 0,
      chargeDirZ: 0,
    };
    this.paintHpBar(enemy);
    this.enemies.push(enemy);
    return enemy;
  }

  private makeBodyMat(def: EnemyDef): MeshStandardMaterial {
    return new MeshStandardMaterial({
      color: def.color,
      roughness: 0.72,
      metalness: 0.1,
      emissive: new Color(def.emissive),
      emissiveIntensity: 0.5,
    });
  }

  private makeAccentMat(hex: number, emissive = 0x000000, intensity = 0.35): MeshStandardMaterial {
    return new MeshStandardMaterial({
      color: hex,
      roughness: 0.45,
      metalness: 0.35,
      emissive: new Color(emissive),
      emissiveIntensity: intensity,
    });
  }

  private makeDarkMat(hex: number): MeshStandardMaterial {
    return new MeshStandardMaterial({
      color: hex,
      roughness: 0.9,
      metalness: 0.05,
      emissive: new Color(0x000000),
      emissiveIntensity: 0,
    });
  }

  private addPart(
    parent: Group,
    geo: BoxGeometry | SphereGeometry | ConeGeometry | CylinderGeometry,
    mat: Material,
    opts: {
      name?: string;
      sx?: number;
      sy?: number;
      sz?: number;
      x?: number;
      y?: number;
      z?: number;
      rx?: number;
      ry?: number;
      rz?: number;
      /** Keep own color through hit-flash restore (eyes, crystals, etc.). */
      accent?: boolean;
    } = {},
  ): Mesh {
    const partMat = mat instanceof MeshStandardMaterial ? mat.clone() : mat;
    const mesh = new Mesh(geo, partMat);
    mesh.name = opts.name ?? '';
    mesh.scale.set(opts.sx ?? 1, opts.sy ?? 1, opts.sz ?? 1);
    mesh.position.set(opts.x ?? 0, opts.y ?? 0, opts.z ?? 0);
    mesh.rotation.set(opts.rx ?? 0, opts.ry ?? 0, opts.rz ?? 0);
    if (partMat instanceof MeshStandardMaterial) {
      mesh.userData.baseColor = partMat.color.getHex();
      mesh.userData.baseEmissive = partMat.emissive.getHex();
      mesh.userData.baseEmissiveIntensity = partMat.emissiveIntensity;
      mesh.userData.accent = opts.accent === true;
    }
    parent.add(mesh);
    return mesh;
  }

  private createMesh(def: EnemyDef): {
    mesh: Group;
    hpSprite: Sprite;
    hpCanvas: HTMLCanvasElement;
    hpCtx: CanvasRenderingContext2D;
    hpTexture: CanvasTexture;
  } {
    const group = new Group();
    const bodyMat = this.makeBodyMat(def);
    const hx = def.halfExtents.x;
    const hy = def.halfExtents.y;
    const hz = def.halfExtents.z;

    switch (def.kind) {
      case EnemyKind.Crawler:
        this.buildCrawler(group, bodyMat, hx, hy, hz);
        break;
      case EnemyKind.Grub:
        this.buildGrub(group, bodyMat, hx, hy, hz);
        break;
      case EnemyKind.Bat:
        this.buildBat(group, bodyMat, hx, hy, hz);
        break;
      case EnemyKind.VaultSentinel:
        this.buildVaultSentinel(group, bodyMat, hx, hy, hz);
        break;
      case EnemyKind.SpireMother:
        this.buildSpireMother(group, bodyMat, hx, hy, hz);
        break;
      case EnemyKind.SpireTotem:
        this.buildSpireTotem(group, bodyMat, hx, hy, hz);
        break;
      case EnemyKind.TunnelHorror:
        this.buildTunnelHorror(group, bodyMat, hx, hy, hz);
        break;
    }

    const flashMat = new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    const flash = new Mesh(this.flashOverlayGeo, flashMat);
    flash.scale.set(hx * 2.15, hy * 2.15, hz * 2.15);
    flash.name = 'hitFlash';
    group.add(flash);

    const canvas = document.createElement('canvas');
    canvas.width = HP_BAR_WIDTH;
    canvas.height = HP_BAR_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2d canvas unavailable for enemy HP bars');
    const texture = new CanvasTexture(canvas);
    texture.needsUpdate = true;
    const spriteMat = new SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: true,
      sizeAttenuation: true,
    });
    const hpSprite = new Sprite(spriteMat);
    const bossBar = isBossKind(def.kind);
    hpSprite.scale.set(
      bossBar ? 1.6 : def.kind === EnemyKind.SpireTotem ? 0.7 : 0.9,
      bossBar ? 0.2 : 0.14,
      1,
    );
    hpSprite.position.y = hy + (bossBar ? 0.75 : 0.5);
    hpSprite.name = 'hpBar';
    group.add(hpSprite);

    return { mesh: group, hpSprite, hpCanvas: canvas, hpCtx: ctx, hpTexture: texture };
  }

  /** Low beetle: flat shell, stub legs, forward snout, glowing eyes. */
  private buildCrawler(
    group: Group,
    bodyMat: MeshStandardMaterial,
    hx: number,
    hy: number,
    hz: number,
  ): void {
    const shell = this.makeAccentMat(0xb86a48, 0x6a3018, 0.4);
    const dark = this.makeDarkMat(0x3a2014);
    const eye = this.makeAccentMat(0xffcc44, 0xffaa22, 1.2);

    this.addPart(group, this.bodyGeo, bodyMat, {
      name: 'body',
      sx: hx * 1.7,
      sy: hy * 1.35,
      sz: hz * 1.8,
      y: hy * 0.15,
    });
    this.addPart(group, this.bodyGeo, shell, {
      name: 'shell',
      sx: hx * 1.95,
      sy: hy * 0.55,
      sz: hz * 1.55,
      y: hy * 0.85,
    });
    this.addPart(group, this.headGeo, bodyMat, {
      name: 'head',
      sx: 0.7,
      sy: 0.55,
      sz: 0.75,
      y: hy * 0.35,
      z: hz * 0.95,
    });
    this.addPart(group, this.coneGeo, dark, {
      name: 'snout',
      sx: 0.28,
      sy: 0.35,
      sz: 0.28,
      y: hy * 0.25,
      z: hz * 1.35,
      rx: Math.PI / 2,
    });
    this.addPart(group, this.headGeo, eye, {
      name: 'eye',
      sx: 0.22,
      sy: 0.22,
      sz: 0.22,
      x: hx * 0.35,
      y: hy * 0.55,
      z: hz * 1.15,
      accent: true,
    });
    this.addPart(group, this.headGeo, eye, {
      name: 'eye',
      sx: 0.22,
      sy: 0.22,
      sz: 0.22,
      x: -hx * 0.35,
      y: hy * 0.55,
      z: hz * 1.15,
      accent: true,
    });

    for (const side of [-1, 1] as const) {
      for (let i = 0; i < 3; i++) {
        const z = hz * (0.55 - i * 0.55);
        this.addPart(group, this.bodyGeo, dark, {
          name: 'leg',
          sx: hx * 0.55,
          sy: hy * 0.35,
          sz: hz * 0.22,
          x: side * hx * 1.15,
          y: -hy * 0.55,
          z,
          rz: side * 0.35,
        });
      }
    }
  }

  /** Fat segmented worm with ridges and a blunt face. */
  private buildGrub(
    group: Group,
    bodyMat: MeshStandardMaterial,
    hx: number,
    hy: number,
    hz: number,
  ): void {
    const ridge = this.makeAccentMat(0x6a7a4a, 0x2a3a18, 0.3);
    const belly = this.makeDarkMat(0x5a6040);
    const eye = this.makeAccentMat(0xccff66, 0x88cc22, 0.9);

    this.addPart(group, this.bodyGeo, bodyMat, {
      name: 'body',
      sx: hx * 1.7,
      sy: hy * 1.5,
      sz: hz * 1.1,
      z: -hz * 0.15,
    });
    this.addPart(group, this.bodyGeo, ridge, {
      name: 'seg',
      sx: hx * 1.55,
      sy: hy * 1.35,
      sz: hz * 0.7,
      z: hz * 0.55,
    });
    this.addPart(group, this.bodyGeo, bodyMat, {
      name: 'seg',
      sx: hx * 1.35,
      sy: hy * 1.15,
      sz: hz * 0.55,
      z: hz * 1.05,
    });
    this.addPart(group, this.headGeo, bodyMat, {
      name: 'head',
      sx: 0.95,
      sy: 0.85,
      sz: 0.9,
      y: hy * 0.1,
      z: hz * 1.35,
    });
    this.addPart(group, this.bodyGeo, belly, {
      name: 'belly',
      sx: hx * 1.2,
      sy: hy * 0.35,
      sz: hz * 1.6,
      y: -hy * 0.7,
      z: hz * 0.2,
    });
    this.addPart(group, this.headGeo, eye, {
      name: 'eye',
      sx: 0.28,
      sy: 0.28,
      sz: 0.28,
      x: hx * 0.4,
      y: hy * 0.35,
      z: hz * 1.55,
      accent: true,
    });
    this.addPart(group, this.headGeo, eye, {
      name: 'eye',
      sx: 0.28,
      sy: 0.28,
      sz: 0.28,
      x: -hx * 0.4,
      y: hy * 0.35,
      z: hz * 1.55,
      accent: true,
    });
    // Dorsal ridges
    for (let i = 0; i < 3; i++) {
      this.addPart(group, this.bodyGeo, ridge, {
        name: 'ridge',
        sx: hx * 0.35,
        sy: hy * 0.4,
        sz: hz * 0.35,
        y: hy * 0.95,
        z: -hz * 0.3 + i * hz * 0.55,
      });
    }
  }

  /** Compact body, glowing eyes, flappable wing planes. */
  private buildBat(
    group: Group,
    bodyMat: MeshStandardMaterial,
    hx: number,
    hy: number,
    hz: number,
  ): void {
    const wingMat = this.makeAccentMat(0x4a3a7a, 0x8822cc, 0.65);
    const dark = this.makeDarkMat(0x2a1a40);
    const eye = this.makeAccentMat(0xff66ee, 0xff44cc, 1.4);

    this.addPart(group, this.bodyGeo, bodyMat, {
      name: 'body',
      sx: hx * 1.4,
      sy: hy * 1.6,
      sz: hz * 1.3,
    });
    this.addPart(group, this.headGeo, bodyMat, {
      name: 'head',
      sx: 0.75,
      sy: 0.7,
      sz: 0.75,
      y: hy * 0.55,
      z: hz * 0.35,
    });
    this.addPart(group, this.coneGeo, dark, {
      name: 'ear',
      sx: 0.22,
      sy: 0.4,
      sz: 0.15,
      x: hx * 0.35,
      y: hy * 1.15,
      z: hz * 0.2,
    });
    this.addPart(group, this.coneGeo, dark, {
      name: 'ear',
      sx: 0.22,
      sy: 0.4,
      sz: 0.15,
      x: -hx * 0.35,
      y: hy * 1.15,
      z: hz * 0.2,
    });
    this.addPart(group, this.headGeo, eye, {
      name: 'eye',
      sx: 0.2,
      sy: 0.2,
      sz: 0.2,
      x: hx * 0.28,
      y: hy * 0.6,
      z: hz * 0.7,
      accent: true,
    });
    this.addPart(group, this.headGeo, eye, {
      name: 'eye',
      sx: 0.2,
      sy: 0.2,
      sz: 0.2,
      x: -hx * 0.28,
      y: hy * 0.6,
      z: hz * 0.7,
      accent: true,
    });

    const wingL = new Group();
    wingL.name = 'wingL';
    wingL.position.set(-hx * 0.7, hy * 0.2, 0);
    this.addPart(wingL, this.bodyGeo, wingMat, {
      name: 'wing',
      sx: hx * 1.8,
      sy: hy * 0.18,
      sz: hz * 1.1,
      x: -hx * 0.9,
      rz: 0.15,
    });
    this.addPart(wingL, this.bodyGeo, wingMat, {
      name: 'wing',
      sx: hx * 1.1,
      sy: hy * 0.12,
      sz: hz * 0.7,
      x: -hx * 1.7,
      y: -hy * 0.15,
      rz: 0.35,
    });
    group.add(wingL);

    const wingR = new Group();
    wingR.name = 'wingR';
    wingR.position.set(hx * 0.7, hy * 0.2, 0);
    this.addPart(wingR, this.bodyGeo, wingMat, {
      name: 'wing',
      sx: hx * 1.8,
      sy: hy * 0.18,
      sz: hz * 1.1,
      x: hx * 0.9,
      rz: -0.15,
    });
    this.addPart(wingR, this.bodyGeo, wingMat, {
      name: 'wing',
      sx: hx * 1.1,
      sy: hy * 0.12,
      sz: hz * 0.7,
      x: hx * 1.7,
      y: -hy * 0.15,
      rz: -0.35,
    });
    group.add(wingR);
  }

  /** Armored golem: plated torso, pauldron shoulders, helm, glowing visor. */
  private buildVaultSentinel(
    group: Group,
    bodyMat: MeshStandardMaterial,
    hx: number,
    hy: number,
    hz: number,
  ): void {
    const plate = this.makeAccentMat(0x8a92a0, 0x405070, 0.45);
    plate.metalness = 0.65;
    plate.roughness = 0.35;
    const dark = this.makeDarkMat(0x2a3038);
    const visor = this.makeAccentMat(0x66aaff, 0x4488ff, 1.3);

    this.addPart(group, this.bodyGeo, bodyMat, {
      name: 'body',
      sx: hx * 1.5,
      sy: hy * 1.35,
      sz: hz * 1.2,
      y: -hy * 0.05,
    });
    this.addPart(group, this.bodyGeo, plate, {
      name: 'chest',
      sx: hx * 1.7,
      sy: hy * 0.7,
      sz: hz * 1.35,
      y: hy * 0.25,
    });
    this.addPart(group, this.bodyGeo, plate, {
      name: 'pauldron',
      sx: hx * 0.7,
      sy: hy * 0.45,
      sz: hz * 0.7,
      x: hx * 1.15,
      y: hy * 0.55,
    });
    this.addPart(group, this.bodyGeo, plate, {
      name: 'pauldron',
      sx: hx * 0.7,
      sy: hy * 0.45,
      sz: hz * 0.7,
      x: -hx * 1.15,
      y: hy * 0.55,
    });
    this.addPart(group, this.bodyGeo, dark, {
      name: 'leg',
      sx: hx * 0.55,
      sy: hy * 0.7,
      sz: hz * 0.55,
      x: hx * 0.45,
      y: -hy * 0.85,
    });
    this.addPart(group, this.bodyGeo, dark, {
      name: 'leg',
      sx: hx * 0.55,
      sy: hy * 0.7,
      sz: hz * 0.55,
      x: -hx * 0.45,
      y: -hy * 0.85,
    });
    this.addPart(group, this.bodyGeo, plate, {
      name: 'helm',
      sx: hx * 1.15,
      sy: hy * 0.55,
      sz: hz * 1.1,
      y: hy * 0.95,
    });
    this.addPart(group, this.bodyGeo, visor, {
      name: 'visor',
      sx: hx * 0.95,
      sy: hy * 0.18,
      sz: hz * 0.25,
      y: hy * 0.95,
      z: hz * 0.55,
      accent: true,
    });
    this.addPart(group, this.coneGeo, plate, {
      name: 'crest',
      sx: 0.35,
      sy: 0.55,
      sz: 0.2,
      y: hy * 1.4,
    });
  }

  /** Floating moth-queen: bulbous abdomen, crown, wing sails. */
  private buildSpireMother(
    group: Group,
    bodyMat: MeshStandardMaterial,
    hx: number,
    hy: number,
    hz: number,
  ): void {
    const wingMat = this.makeAccentMat(0x7a4aaa, 0xaa44dd, 0.75);
    const crown = this.makeAccentMat(0xcc66ff, 0xff44ee, 1.1);
    const dark = this.makeDarkMat(0x2a1838);
    const eye = this.makeAccentMat(0xff88ff, 0xff44aa, 1.2);

    this.addPart(group, this.bodyGeo, bodyMat, {
      name: 'body',
      sx: hx * 1.4,
      sy: hy * 1.2,
      sz: hz * 1.2,
      y: hy * 0.1,
    });
    this.addPart(group, this.headGeo, bodyMat, {
      name: 'abdomen',
      sx: 1.6,
      sy: 1.4,
      sz: 1.5,
      y: -hy * 0.35,
      z: -hz * 0.35,
    });
    this.addPart(group, this.headGeo, bodyMat, {
      name: 'head',
      sx: 1.0,
      sy: 0.9,
      sz: 1.0,
      y: hy * 0.75,
      z: hz * 0.25,
    });
    this.addPart(group, this.coneGeo, crown, {
      name: 'horn',
      sx: 0.35,
      sy: 0.7,
      sz: 0.35,
      x: hx * 0.35,
      y: hy * 1.25,
      z: hz * 0.1,
      accent: true,
    });
    this.addPart(group, this.coneGeo, crown, {
      name: 'horn',
      sx: 0.35,
      sy: 0.7,
      sz: 0.35,
      x: -hx * 0.35,
      y: hy * 1.25,
      z: hz * 0.1,
      accent: true,
    });
    this.addPart(group, this.headGeo, eye, {
      name: 'eye',
      sx: 0.35,
      sy: 0.35,
      sz: 0.35,
      x: hx * 0.35,
      y: hy * 0.8,
      z: hz * 0.7,
      accent: true,
    });
    this.addPart(group, this.headGeo, eye, {
      name: 'eye',
      sx: 0.35,
      sy: 0.35,
      sz: 0.35,
      x: -hx * 0.35,
      y: hy * 0.8,
      z: hz * 0.7,
      accent: true,
    });
    this.addPart(group, this.bodyGeo, dark, {
      name: 'mandible',
      sx: hx * 0.25,
      sy: hy * 0.2,
      sz: hz * 0.4,
      x: hx * 0.25,
      y: hy * 0.45,
      z: hz * 0.85,
      rz: 0.3,
    });
    this.addPart(group, this.bodyGeo, dark, {
      name: 'mandible',
      sx: hx * 0.25,
      sy: hy * 0.2,
      sz: hz * 0.4,
      x: -hx * 0.25,
      y: hy * 0.45,
      z: hz * 0.85,
      rz: -0.3,
    });

    const wingL = new Group();
    wingL.name = 'wingL';
    wingL.position.set(-hx * 0.6, hy * 0.3, -hz * 0.1);
    this.addPart(wingL, this.bodyGeo, wingMat, {
      name: 'wing',
      sx: hx * 1.6,
      sy: hy * 1.4,
      sz: hz * 0.12,
      x: -hx * 1.0,
      rz: 0.25,
    });
    group.add(wingL);

    const wingR = new Group();
    wingR.name = 'wingR';
    wingR.position.set(hx * 0.6, hy * 0.3, -hz * 0.1);
    this.addPart(wingR, this.bodyGeo, wingMat, {
      name: 'wing',
      sx: hx * 1.6,
      sy: hy * 1.4,
      sz: hz * 0.12,
      x: hx * 1.0,
      rz: -0.25,
    });
    group.add(wingR);
  }

  /** Runic pillar with glowing crystal tip and band accents. */
  private buildSpireTotem(
    group: Group,
    bodyMat: MeshStandardMaterial,
    hx: number,
    hy: number,
    hz: number,
  ): void {
    const band = this.makeAccentMat(0x6a4a88, 0xaa44ff, 0.7);
    const crystal = this.makeAccentMat(0xee88ff, 0xff66ff, 1.5);
    const dark = this.makeDarkMat(0x1a1028);

    this.addPart(group, this.cylGeo, bodyMat, {
      name: 'body',
      sx: hx * 1.6,
      sy: hy * 1.7,
      sz: hz * 1.6,
      y: -hy * 0.05,
    });
    this.addPart(group, this.cylGeo, dark, {
      name: 'base',
      sx: hx * 2.0,
      sy: hy * 0.25,
      sz: hz * 2.0,
      y: -hy * 0.95,
    });
    this.addPart(group, this.cylGeo, band, {
      name: 'band',
      sx: hx * 1.75,
      sy: hy * 0.18,
      sz: hz * 1.75,
      y: hy * 0.15,
    });
    this.addPart(group, this.cylGeo, band, {
      name: 'band',
      sx: hx * 1.7,
      sy: hy * 0.14,
      sz: hz * 1.7,
      y: -hy * 0.4,
    });
    this.addPart(group, this.coneGeo, crystal, {
      name: 'crystal',
      sx: hx * 1.3,
      sy: hy * 0.9,
      sz: hz * 1.3,
      y: hy * 1.05,
      accent: true,
    });
    this.addPart(group, this.headGeo, crystal, {
      name: 'orb',
      sx: 0.55,
      sy: 0.55,
      sz: 0.55,
      y: hy * 0.55,
      z: hz * 0.85,
      accent: true,
    });
  }

  /** Long burrowing beast: segmented body, jaws, dorsal spines. */
  private buildTunnelHorror(
    group: Group,
    bodyMat: MeshStandardMaterial,
    hx: number,
    hy: number,
    hz: number,
  ): void {
    const plate = this.makeAccentMat(0x6a4030, 0xaa3310, 0.55);
    const dark = this.makeDarkMat(0x2a1810);
    const fang = this.makeAccentMat(0xd8c8a0, 0x886644, 0.3);
    const eye = this.makeAccentMat(0xff4422, 0xff2200, 1.4);

    this.addPart(group, this.bodyGeo, bodyMat, {
      name: 'body',
      sx: hx * 1.5,
      sy: hy * 1.4,
      sz: hz * 1.2,
      z: -hz * 0.15,
    });
    this.addPart(group, this.bodyGeo, plate, {
      name: 'seg',
      sx: hx * 1.35,
      sy: hy * 1.2,
      sz: hz * 0.7,
      z: hz * 0.55,
    });
    this.addPart(group, this.headGeo, bodyMat, {
      name: 'head',
      sx: 1.3,
      sy: 1.0,
      sz: 1.2,
      y: hy * 0.1,
      z: hz * 1.05,
    });
    this.addPart(group, this.bodyGeo, dark, {
      name: 'jaw',
      sx: hx * 1.0,
      sy: hy * 0.35,
      sz: hz * 0.55,
      y: -hy * 0.35,
      z: hz * 1.35,
    });
    this.addPart(group, this.coneGeo, fang, {
      name: 'fang',
      sx: 0.25,
      sy: 0.45,
      sz: 0.25,
      x: hx * 0.35,
      y: -hy * 0.15,
      z: hz * 1.55,
      rx: Math.PI / 2,
    });
    this.addPart(group, this.coneGeo, fang, {
      name: 'fang',
      sx: 0.25,
      sy: 0.45,
      sz: 0.25,
      x: -hx * 0.35,
      y: -hy * 0.15,
      z: hz * 1.55,
      rx: Math.PI / 2,
    });
    this.addPart(group, this.headGeo, eye, {
      name: 'eye',
      sx: 0.35,
      sy: 0.28,
      sz: 0.28,
      x: hx * 0.55,
      y: hy * 0.45,
      z: hz * 1.2,
      accent: true,
    });
    this.addPart(group, this.headGeo, eye, {
      name: 'eye',
      sx: 0.35,
      sy: 0.28,
      sz: 0.28,
      x: -hx * 0.55,
      y: hy * 0.45,
      z: hz * 1.2,
      accent: true,
    });

    for (let i = 0; i < 4; i++) {
      this.addPart(group, this.coneGeo, plate, {
        name: 'spine',
        sx: 0.3,
        sy: 0.55 + i * 0.05,
        sz: 0.25,
        y: hy * 0.95,
        z: -hz * 0.5 + i * hz * 0.4,
      });
    }

    for (const side of [-1, 1] as const) {
      for (let i = 0; i < 2; i++) {
        this.addPart(group, this.bodyGeo, dark, {
          name: 'leg',
          sx: hx * 0.4,
          sy: hy * 0.5,
          sz: hz * 0.25,
          x: side * hx * 1.05,
          y: -hy * 0.7,
          z: -hz * 0.2 + i * hz * 0.55,
          rz: side * 0.4,
        });
      }
    }
  }

  private paintHpBar(e: EnemyEntity): void {
    const { hpCtx: ctx, hpCanvas: canvas, hpTexture } = e;
    const w = canvas.width;
    const h = canvas.height;
    const frac = Math.max(0, Math.min(1, e.health / e.maxHealth));

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = 'rgba(0,0,0,0.7)';
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, w - 2, h - 2);

    const pad = 3;
    const innerW = w - pad * 2;
    const fillW = Math.max(0, Math.round(innerW * frac));
    const r = frac > 0.5 ? Math.round(255 * (1 - (frac - 0.5) * 2)) : 255;
    const g = frac > 0.5 ? 200 : Math.round(200 * (frac / 0.5));
    ctx.fillStyle = `rgb(${r},${g},48)`;
    ctx.fillRect(pad, pad, fillW, h - pad * 2);

    hpTexture.needsUpdate = true;
  }

  private syncMesh(e: EnemyEntity): void {
    if (e.deathTimer > 0) {
      this.syncDeathMesh(e);
      return;
    }

    const bob =
      e.flying && !e.burrowed
        ? Math.sin(this.animTime * 3.2 + e.position.x * 0.4) * 0.08
        : e.kind === EnemyKind.SpireTotem
          ? Math.sin(this.animTime * 2.0) * 0.03
          : e.kind === EnemyKind.Grub
            ? Math.sin(this.animTime * 4.0 + e.position.z) * 0.025
            : 0;
    const yOff = (e.burrowed ? -e.halfExtents.y * 0.85 : 0) + bob;
    e.mesh.position.set(e.position.x, e.position.y + yOff, e.position.z);
    e.mesh.visible = true;
    if (e.burrowed) {
      e.mesh.scale.setScalar(0.55);
    } else {
      e.mesh.scale.setScalar(1);
    }
    e.mesh.rotation.x = 0;
    e.mesh.rotation.z = 0;

    if (e.stunTimer <= 0 && (e.velocity.x !== 0 || e.velocity.z !== 0)) {
      e.mesh.rotation.y = Math.atan2(e.velocity.x, e.velocity.z);
    }

    // Wing flap / idle motion on named child groups
    const flap = Math.sin(this.animTime * (e.kind === EnemyKind.Bat ? 10 : 4.5)) * 0.45;
    const wingL = e.mesh.getObjectByName('wingL');
    const wingR = e.mesh.getObjectByName('wingR');
    if (wingL) wingL.rotation.z = flap;
    if (wingR) wingR.rotation.z = -flap;

    if (e.kind === EnemyKind.Crawler) {
      const walk = Math.sin(this.animTime * 8 + e.position.x) * 0.12;
      e.mesh.traverse((obj) => {
        if (obj.name === 'leg') {
          (obj as Mesh).rotation.x = walk * ((obj as Mesh).position.z > 0 ? 1 : -1);
        }
      });
    }

    if (e.kind === EnemyKind.SpireTotem) {
      const pulse = 0.9 + Math.sin(this.animTime * 3.5) * 0.35;
      e.mesh.traverse((obj) => {
        const mesh = obj as Mesh;
        if (!mesh.isMesh) return;
        if (mesh.name === 'crystal' || mesh.name === 'orb' || mesh.name === 'band') {
          const mat = mesh.material as MeshStandardMaterial;
          if (mat?.isMeshStandardMaterial) mat.emissiveIntensity = pulse;
        }
      });
    }

    const flashing = e.hitFlash > 0 || e.telegraphTimer > 0;
    const flashT = e.hitFlash > 0 ? e.hitFlash / HIT_FLASH_DURATION : 0.7;

    e.mesh.traverse((obj) => {
      const mesh = obj as Mesh;
      if (!mesh.isMesh) return;

      if (mesh.name === 'hitFlash') {
        const mat = mesh.material as MeshBasicMaterial;
        mat.opacity = flashing ? 0.55 + flashT * 0.35 : 0;
        mesh.visible = flashing;
        return;
      }

      const mat = mesh.material as MeshStandardMaterial;
      if (!mat?.isMeshStandardMaterial) return;

      const baseColor = (mesh.userData.baseColor as number | undefined) ?? e.baseColor;
      const baseEmissive = (mesh.userData.baseEmissive as number | undefined) ?? e.baseEmissive;
      const baseIntensity = (mesh.userData.baseEmissiveIntensity as number | undefined) ?? 0.5;
      const isAccent = mesh.userData.accent === true;

      if (flashing) {
        mat.emissive.setHex(0xfff0e0);
        mat.emissiveIntensity = 1.6 + flashT * 1.2;
        mat.color.setHex(0xffffff);
      } else if (e.burrowed) {
        mat.emissive.setHex(0x221100);
        mat.emissiveIntensity = 0.15;
        mat.color.setHex(0x2a1a10);
      } else if (e.armorCracked && !isAccent) {
        mat.color.setHex(baseColor);
        mat.emissive.setHex(0xffaa44);
        mat.emissiveIntensity = 1.15;
      } else if (e.invulnerable && !isAccent) {
        mat.color.setHex(baseColor);
        mat.emissive.setHex(0x8866ff);
        mat.emissiveIntensity = 1.0;
      } else {
        mat.color.setHex(baseColor);
        mat.emissive.setHex(baseEmissive);
        if (!(e.kind === EnemyKind.SpireTotem && (mesh.name === 'crystal' || mesh.name === 'orb' || mesh.name === 'band'))) {
          mat.emissiveIntensity = baseIntensity;
        }
      }
    });

    e.hpSprite.position.y =
      e.halfExtents.y + (isBossKind(e.kind) ? 0.75 : 0.5) + (e.flying ? 0.1 : 0);
    e.hpSprite.visible = !e.burrowed;
  }

  /** Tip over, shrink, and fade a dying corpse. */
  private syncDeathMesh(e: EnemyEntity): void {
    const duration = (e.mesh.userData.deathDuration as number) || DEATH_ANIM_DURATION;
    const t = 1 - Math.max(0, e.deathTimer) / duration;
    const ease = t * t * (3 - 2 * t);
    const side = (e.mesh.userData.deathSide as number) || 1;
    const yaw = (e.mesh.userData.deathYaw as number) ?? e.mesh.rotation.y;

    const pop = Math.sin(Math.min(1, t * 2.2) * Math.PI) * e.halfExtents.y * 0.35;
    e.mesh.position.set(e.position.x, e.position.y + pop * (1 - ease), e.position.z);
    e.mesh.rotation.y = yaw;
    e.mesh.rotation.x = ease * (e.flying ? Math.PI * 0.85 : Math.PI * 0.55) * side;
    e.mesh.rotation.z = ease * 0.55 * -side;
    const scale = Math.max(0.04, 1 - ease * 0.92);
    e.mesh.scale.setScalar(scale);
    e.mesh.visible = scale > 0.05;
    e.hpSprite.visible = false;

    const flashing = e.hitFlash > 0;
    const flashT = flashing ? e.hitFlash / HIT_FLASH_DURATION : 0;
    const fade = 1 - ease;

    e.mesh.traverse((obj) => {
      const mesh = obj as Mesh;
      if (!mesh.isMesh) return;

      if (mesh.name === 'hitFlash') {
        const mat = mesh.material as MeshBasicMaterial;
        mat.transparent = true;
        mat.opacity = flashing ? (0.5 + flashT * 0.4) * fade : 0;
        mesh.visible = flashing && fade > 0.05;
        return;
      }

      const mat = mesh.material as MeshStandardMaterial | MeshBasicMaterial;
      if (!mat) return;
      mat.transparent = true;
      mat.depthWrite = fade > 0.35;
      mat.opacity = fade;

      if ('emissive' in mat && mat.isMeshStandardMaterial) {
        if (flashing || t < 0.28) {
          mat.emissive.setHex(0xfff2dd);
          mat.emissiveIntensity = 1.8 * (1 - t);
          mat.color.setHex(0xffffff);
        } else {
          const baseColor = (mesh.userData.baseColor as number | undefined) ?? e.baseColor;
          mat.color.setHex(baseColor);
          mat.emissive.setHex(0x000000);
          mat.emissiveIntensity = 0;
        }
      }
    });
  }

  private destroyEnemy(e: EnemyEntity): void {
    const idx = this.enemies.indexOf(e);
    if (idx >= 0) this.enemies.splice(idx, 1);
    e.hpTexture.dispose();
    (e.hpSprite.material as SpriteMaterial).dispose();
    e.mesh.traverse((obj) => {
      const mesh = obj as Mesh;
      if (mesh.isMesh) {
        const mat = mesh.material;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else (mat as MeshStandardMaterial | MeshBasicMaterial).dispose?.();
      }
    });
    e.mesh.removeFromParent();
  }

  private separateFromPlayers(
    e: EnemyEntity,
    targets: ReadonlyArray<{ id: string; position: Vector3D }>,
  ): void {
    if (e.burrowed) return;
    const minDist = stopDistance(e.halfExtents.x);
    for (const t of targets) {
      const dx = e.position.x - t.position.x;
      const dz = e.position.z - t.position.z;
      const horiz = Math.hypot(dx, dz);
      if (horiz >= minDist) continue;

      if (horiz < EPSILON) {
        e.position.x += minDist;
      } else {
        const push = (minDist - horiz) / horiz;
        e.position.x += dx * push;
        e.position.z += dz * push;
      }

      const toPlayerX = t.position.x - e.position.x;
      const toPlayerZ = t.position.z - e.position.z;
      const inward = e.velocity.x * toPlayerX + e.velocity.z * toPlayerZ;
      if (inward > 0) {
        const lenSq = toPlayerX * toPlayerX + toPlayerZ * toPlayerZ;
        if (lenSq > EPSILON) {
          const inv = inward / lenSq;
          e.velocity.x -= toPlayerX * inv;
          e.velocity.z -= toPlayerZ * inv;
        }
      }
    }
  }

  /** Soft circle packing so living mobs do not stack on the same spot. */
  private separateEnemies(): void {
    const list = this.enemies;
    const n = list.length;
    // Two passes keeps dense packs from remaining nested after one shove.
    for (let pass = 0; pass < 2; pass++) {
      for (let i = 0; i < n; i++) {
        const a = list[i]!;
        if (!this.canSeparate(a)) continue;
        for (let j = i + 1; j < n; j++) {
          const b = list[j]!;
          if (!b.alive || b.burrowed || b.deathTimer > 0) continue;

          const dy = a.position.y - b.position.y;
          if (Math.abs(dy) >= a.halfExtents.y + b.halfExtents.y) continue;

          const dx = a.position.x - b.position.x;
          const dz = a.position.z - b.position.z;
          const dist = Math.hypot(dx, dz);
          const minDist = a.halfExtents.x + b.halfExtents.x + PACK_SEPARATION_PADDING;
          if (dist >= minDist) continue;

          const bStatic = b.kind === EnemyKind.SpireTotem;
          if (dist < EPSILON) {
            if (bStatic) {
              a.position.x += minDist;
            } else {
              a.position.x += minDist * 0.5;
              b.position.x -= minDist * 0.5;
            }
            continue;
          }

          const nx = dx / dist;
          const nz = dz / dist;
          const overlap = minDist - dist;

          if (bStatic) {
            a.position.x += nx * overlap;
            a.position.z += nz * overlap;
            this.cancelInwardVelocity(a, -nx, -nz);
          } else {
            const half = overlap * 0.5;
            a.position.x += nx * half;
            a.position.z += nz * half;
            b.position.x -= nx * half;
            b.position.z -= nz * half;
            this.cancelInwardVelocity(a, -nx, -nz);
            this.cancelInwardVelocity(b, nx, nz);
          }
        }
      }
    }
  }

  private canSeparate(e: EnemyEntity): boolean {
    return e.alive && !e.burrowed && e.deathTimer <= 0 && e.kind !== EnemyKind.SpireTotem;
  }

  /** Remove velocity toward another body after a separation shove. */
  private cancelInwardVelocity(e: EnemyEntity, towardX: number, towardZ: number): void {
    const inward = e.velocity.x * towardX + e.velocity.z * towardZ;
    if (inward <= 0) return;
    const lenSq = towardX * towardX + towardZ * towardZ;
    if (lenSq < EPSILON) return;
    const inv = inward / lenSq;
    e.velocity.x -= towardX * inv;
    e.velocity.z -= towardZ * inv;
  }

  private integrateEnemy(e: EnemyEntity, dt: number): void {
    if (e.burrowed) {
      // Soft move without solid collision while underground.
      e.position.x += e.velocity.x * dt;
      e.position.z += e.velocity.z * dt;
      e.onGround = false;
      return;
    }
    const dx = e.velocity.x * dt;
    const dy = e.velocity.y * dt;
    const dz = e.velocity.z * dt;
    e.onGround = false;
    if (dx !== 0) this.moveAxis(e, 'x', dx);
    if (dz !== 0) this.moveAxis(e, 'z', dz);
    if (dy !== 0) this.moveAxis(e, 'y', dy);
    else if (!e.flying) this.probeGround(e);
  }

  private moveAxis(e: EnemyEntity, axis: 'x' | 'y' | 'z', delta: number): void {
    const pos = e.position;
    const he = e.halfExtents;
    const bs = this.blockSize;
    pos[axis] += delta;

    const minX = pos.x - he.x;
    const maxX = pos.x + he.x;
    const minY = pos.y - he.y;
    const maxY = pos.y + he.y;
    const minZ = pos.z - he.z;
    const maxZ = pos.z + he.z;

    const vx0 = Math.floor(minX / bs);
    const vx1 = Math.floor((maxX - EPSILON) / bs);
    const vy0 = Math.floor(minY / bs);
    const vy1 = Math.floor((maxY - EPSILON) / bs);
    const vz0 = Math.floor(minZ / bs);
    const vz1 = Math.floor((maxZ - EPSILON) / bs);

    for (let y = vy0; y <= vy1; y++) {
      for (let z = vz0; z <= vz1; z++) {
        for (let x = vx0; x <= vx1; x++) {
          if (!this.world.isSolid(x, y, z)) continue;
          const bMinX = x * bs;
          const bMaxX = bMinX + bs;
          const bMinY = y * bs;
          const bMaxY = bMinY + bs;
          const bMinZ = z * bs;
          const bMaxZ = bMinZ + bs;
          if (maxX <= bMinX || minX >= bMaxX || maxY <= bMinY || minY >= bMaxY || maxZ <= bMinZ || minZ >= bMaxZ) {
            continue;
          }
          if (axis === 'x') {
            pos.x = delta > 0 ? bMinX - he.x - EPSILON : bMaxX + he.x + EPSILON;
            e.velocity.x = 0;
          } else if (axis === 'z') {
            pos.z = delta > 0 ? bMinZ - he.z - EPSILON : bMaxZ + he.z + EPSILON;
            e.velocity.z = 0;
          } else {
            if (delta < 0) {
              pos.y = bMaxY + he.y + EPSILON;
              e.velocity.y = 0;
              e.onGround = true;
            } else {
              pos.y = bMinY - he.y - EPSILON;
              e.velocity.y = 0;
            }
          }
        }
      }
    }
  }

  private probeGround(e: EnemyEntity): void {
    const pos = e.position;
    const he = e.halfExtents;
    const bs = this.blockSize;
    const feetY = pos.y - he.y - 0.05;
    const y = Math.floor(feetY / bs);
    const x0 = Math.floor((pos.x - he.x) / bs);
    const x1 = Math.floor((pos.x + he.x - EPSILON) / bs);
    const z0 = Math.floor((pos.z - he.z) / bs);
    const z1 = Math.floor((pos.z + he.z - EPSILON) / bs);
    for (let x = x0; x <= x1; x++) {
      for (let z = z0; z <= z1; z++) {
        if (this.world.isSolid(x, y, z)) {
          e.onGround = true;
          return;
        }
      }
    }
  }
}

/** Slab method ray vs AABB. Returns entry distance or null. */
function rayAabbDistance(
  origin: Vector3D,
  dir: Vector3D,
  min: Vector3D,
  max: Vector3D,
  maxDistance: number,
): number | null {
  let tMin = 0;
  let tMax = maxDistance;

  for (const axis of ['x', 'y', 'z'] as const) {
    const o = origin[axis];
    const d = dir[axis];
    const bMin = min[axis];
    const bMax = max[axis];
    if (Math.abs(d) < 1e-8) {
      if (o < bMin || o > bMax) return null;
      continue;
    }
    const inv = 1 / d;
    let t1 = (bMin - o) * inv;
    let t2 = (bMax - o) * inv;
    if (t1 > t2) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
    }
    tMin = Math.max(tMin, t1);
    tMax = Math.min(tMax, t2);
    if (tMin > tMax) return null;
  }
  return tMin;
}
